import type { ReactElement } from "react"
import { useState } from "react"

import { useMutation } from "react-query"
import { useNavigate, useParams } from "react-router-dom"

import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { CreateRetrainingModelRequest } from "../../../types/custom/projects"
import type { RetrainingJob } from "../../../types/generated/api/RetrainingJob"
import { RetrainingCreationDialog } from "../../Training/components/RetrainingCreationCard"
import type { RetrainDialogProps } from "../interfaces"

/**
 * Dialog component that handles model retraining functionality
 *
 * @param {boolean} isOpen - Controls the visibility state of the dialog
 * @param {Dispatch} setIsOpen - Function to handle dialog close state
 * @returns {ReactElement} Retrain button with associated dialog
 */
export function RetrainDialog(props: Readonly<RetrainDialogProps>): ReactElement {
  const { isOpen, setIsOpen } = props

  const { id: projectId } = useParams<{ id: string }>()

  const navigate = useNavigate()

  const [errorMessage, setErrorMessage] = useState<string>()

  // Mutation for creating a retraining model
  // Includes success/error handling and navigation
  const createRetrainingModelMutation = useMutation<
    AxiosResponse<RetrainingJob>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    CreateRetrainingModelRequest
  >(KonanAPI.createRetrainingModel, {
    onSuccess: async () => {
      setIsOpen(false)

      await queryClient.invalidateQueries("retrainingModels")

      navigate({
        pathname: `/projects/${projectId}/model-training`,
        search: "tab=inProgress",
      })
    },
  })

  /**
   * Handles the creation of a retraining model
   * @param {string} predictions_start_time - Start time for predictions
   * @param {string} predictions_end_time - End time for predictions
   * @param {boolean} append_training_data - Flag to append training data
   * @param {string} resulting_model_name - Name for the resulting model
   * @param {string} resulting_model_state - State for the resulting model
   */
  const handleRetrainingModelCreation = (
    predictions_start_time: string,
    predictions_end_time: string,
    append_training_data: boolean,
    resulting_model_name: string,
    resulting_model_state: string,
  ): void => {
    createRetrainingModelMutation.mutateAsync({
      predictions_start_time,
      predictions_end_time,
      append_training_data,
      resulting_model_name,
      resulting_model_state,
      project_uuid: projectId as string,
    })
  }

  // Handle dialog close and error reset
  const handleCreationDialogClose = (): void => {
    setIsOpen(false)
    setErrorMessage(undefined)
  }
  return (
    // Model retraining dialog
    // Allows creation of a retraining job for the live current model

    <RetrainingCreationDialog
      open={isOpen}
      setOpen={setIsOpen}
      onClose={handleCreationDialogClose}
      onModelCreation={handleRetrainingModelCreation}
      retrainingError={errorMessage}
      isLoading={createRetrainingModelMutation.isLoading}
    />
  )
}
